import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, API_CONFIG, createFormDataRequest } from '../utils/api';
import './MobileUpload.css';

const MobileUpload = () => {
  const { t } = useTranslation();
  
  // State management - organized by feature
  const [currentSection, setCurrentSection] = useState('property');
  const [navigationHistory, setNavigationHistory] = useState(['property']);

  // Task state
  const [taskType, setTaskType] = useState('');
  const [customTaskType, setCustomTaskType] = useState('');
  const [sessionContext, setSessionContext] = useState(null);

  // Property state
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [unitNumber, setUnitNumber] = useState('');
  const [isUnitMatching, setIsUnitMatching] = useState(false);
  const [unitStatus, setUnitStatus] = useState({
    message: t('property.gettingLocation'),
    type: 'info'
  });
  const [showNoMatchConfirmation, setShowNoMatchConfirmation] = useState(false);
  const [pendingUnitInput, setPendingUnitInput] = useState('');
  const [showAddressInput, setShowAddressInput] = useState(false);
  const [fullAddress, setFullAddress] = useState('');

  // Upload state
  const [uploadedImages, setUploadedImages] = useState([]);

  // UI state
  const [statusMessages, setStatusMessages] = useState([]);
  const [buttonStates, setButtonStates] = useState({});
  const [progressTimers, setProgressTimers] = useState({});

  // Refs
  const photoInputRef = useRef(null);
  
  // Constants
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

  // Utility functions
  // Add debouncing to prevent duplicate notifications
  const recentMessages = useRef(new Set());
  
  const showStatus = useCallback((message, type = 'info', timeout = 5000) => {
    // Create a key for debouncing (message + type combination)
    const messageKey = `${message}-${type}`;
    
    // If this exact message was shown recently, ignore it
    if (recentMessages.current.has(messageKey)) {
      return null;
    }
    
    // Add to recent messages and clean up after a short delay
    recentMessages.current.add(messageKey);
    setTimeout(() => {
      recentMessages.current.delete(messageKey);
    }, 1000); // Prevent duplicate messages for 1 second
    
    const id = Date.now() + Math.random(); // Make ID more unique
    const status = { id, message, type, timeout };
    setStatusMessages(prev => [...prev, status]);
    
    if (timeout > 0) {
      // Set up progress tracking for this notification
      const startTime = Date.now();
      
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / timeout) * 100, 100);
        
        setProgressTimers(prev => ({ ...prev, [id]: progress }));
        
        if (progress < 100) {
          requestAnimationFrame(updateProgress);
        } else {
          // Clean up progress timer
          setProgressTimers(prev => {
            const newTimers = { ...prev };
            delete newTimers[id];
            return newTimers;
          });
        }
      };
      
      // Start progress animation
      requestAnimationFrame(updateProgress);
      
      // Auto-dismiss after timeout
      setTimeout(() => {
        setStatusMessages(prev => prev.filter(s => s.id !== id));
        setProgressTimers(prev => {
          const newTimers = { ...prev };
          delete newTimers[id];
          return newTimers;
        });
      }, timeout);
    }
    
    return id;
  }, []);

  const dismissStatus = useCallback((id) => {
    setStatusMessages(prev => prev.filter(s => s.id !== id));
    // Clean up progress timer when manually dismissed
    setProgressTimers(prev => {
      const newTimers = { ...prev };
      delete newTimers[id];
      return newTimers;
    });
  }, []);

  const updateButtonState = useCallback((buttonKey, isLoading = false, customText = null) => {
    setButtonStates(prev => ({
      ...prev,
      [buttonKey]: { isLoading, customText }
    }));
  }, []);

  // Navigation helpers
  const navigateToSection = useCallback((section) => {
    setNavigationHistory(prev => [...prev, section]);
    setCurrentSection(section);
  }, []);

  const goBack = useCallback(() => {
    setNavigationHistory(prev => {
      if (prev.length > 1) {
        const newHistory = prev.slice(0, -1);
        const previousSection = newHistory[newHistory.length - 1];
        setCurrentSection(previousSection);
        return newHistory;
      }
      return prev;
    });
  }, []);

  const getDeviceInfo = useCallback(() => {
    const deviceInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      pixelRatio: window.devicePixelRatio || 1,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    // Add platform info if available
    if (navigator.userAgentData) {
      deviceInfo.platform = navigator.userAgentData.platform;
    } else if (navigator.platform) {
      deviceInfo.platform = navigator.platform;
    }
    
    return JSON.stringify(deviceInfo);
  }, []);

  // Location services
  const requestLocation = useCallback(async () => {
    try {
      if (!navigator.geolocation) {
        throw new Error(t('errors.geolocationNotSupported'));
      }

      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000, // Reduced from 10 seconds to 5 seconds
          maximumAge: 300000 // 5 minutes
        });
      });

      setUserLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy
      });

      console.log('Location obtained:', position.coords);
      setUnitStatus({
        message: t('property.searchingNearby'),
        type: 'info'
      });

    } catch (error) {
      console.error('Location error:', error);
      let message = 'Location access denied.';

      if (error.code === 1) {
        message = t('property.locationPermissionDenied');
      } else if (error.code === 2) {
        message = t('property.locationUnavailable');
      } else if (error.code === 3) {
        message = t('property.locationTimeout');
      }

      showStatus(message, 'warning', 10000);
      
      // If in property section and location failed, show unit input
      if (currentSection === 'property') {
        setUnitStatus({
          message: t('property.locationUnavailableEnterUnit'),
          type: 'warning'
        });
      }
    }
  }, [showStatus, currentSection]);

  // Check HTTPS requirement
  const checkHttpsRequirement = useCallback(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' || 
                       window.location.hostname.includes('192.168');
    const isHttps = window.location.protocol === 'https:';

    if (isIOS && !isHttps && !isLocalhost) {
      showStatus(t('status.httpsRequired'), 'warning', 15000);
    }

    console.log(`iOS: ${isIOS}, HTTPS: ${isHttps}, Localhost: ${isLocalhost}`);
  }, [showStatus]);

  // Track if initial welcome message has been shown
  const initialWelcomeShown = useRef(false);
  
  // Initialize
  useEffect(() => {
    checkHttpsRequirement();
    requestLocation();
    
    // Only show welcome message once
    if (!initialWelcomeShown.current) {
      showStatus(t('status.welcome'), 'info', 4000);
      initialWelcomeShown.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load property matches when location is available
  useEffect(() => {
    if (userLocation) {
      loadPropertyMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation]);

  // Handle case where location is not available after some time
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentSection === 'property' && !userLocation && !selectedProperty && unitStatus.type === 'info') {
        setUnitStatus({
          message: t('property.locationUnavailableEnterUnit'),
          type: 'warning'
        });
      }
    }, 1000); // Wait 1 second for location (reduced from 3 seconds)

    return () => clearTimeout(timer);
  }, [currentSection, userLocation, selectedProperty, unitStatus.type]);

  // Removed automatic existing images notification - users can see images directly in interface

  // Helper function to generate session context
  const generateSessionContext = (propertyAddress, taskType, customType = null) => {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    return {
      property_address: propertyAddress,
      task_type: taskType,
      task_date: today,
      custom_type: customType
    };
  };

  // Helper function to load existing images for context
  const loadExistingImages = async (context) => {
    try {
      const params = new URLSearchParams({
        property_address: context.property_address,
        task_type: context.task_type,
        task_date: context.task_date
      });
      
      const response = await apiClient.get(`${API_CONFIG.UPLOAD_BASE}/images?${params}`);
      setUploadedImages(response.data);
      console.log(`Loaded ${response.data.length} existing images for context`);
      return response.data.length; // Return count for immediate use
    } catch (error) {
      console.error('Failed to load existing images:', error);
      setUploadedImages([]);
      return 0;
    }
  };

  // Sessionless task management
  const startTaskWithType = async (taskType, customType = null) => {
    updateButtonState('startSession', true);

    try {
      if (!selectedProperty?.display_name) {
        throw new Error(t('errors.propertyMustBeSelected'));
      }

      // Generate session context
      const context = generateSessionContext(selectedProperty.display_name, taskType, customType);
      setSessionContext(context);
      
      // Load any existing images for this context
      const imageCount = await loadExistingImages(context);
      
      console.log('Started task with context:', context);
      if (imageCount > 0) {
        showStatus(`Started ${taskType.replace('-', ' ')} task. Loaded ${imageCount} existing images.`, 'success');
      } else {
        showStatus(`Started ${taskType.replace('-', ' ')} task.`, 'success');
      }
      navigateToSection('camera');

    } catch (error) {
      console.error('Task start error:', error);
      showStatus(`${t('errors.failedToStartTask')}: ${error.message}`, 'error');
    } finally {
      updateButtonState('startSession', false);
    }
  };

  const startTask = async () => {
    if (!taskType) {
      showStatus(t('status.selectTaskType'), 'error');
      return;
    }

    if (taskType === 'other' && !customTaskType.trim()) {
      showStatus(t('status.enterCustomTaskType'), 'error');
      return;
    }

    // Use the helper function for consistency
    return startTaskWithType(taskType, taskType === 'other' ? customTaskType.trim() : null);
  };

  const loadPropertyMatches = useCallback(async () => {
    if (!userLocation) {
      // No geolocation available, show unit number input
      setUnitStatus({
        message: t('property.locationUnavailableEnterUnit'),
        type: 'warning'
      });
      return;
    }

    // Try to match property using geolocation
    try {
      const formData = new FormData();
      formData.append('latitude', userLocation.latitude);
      formData.append('longitude', userLocation.longitude);
      formData.append('radius_meters', '200');

      const response = await createFormDataRequest(`${API_CONFIG.UPLOAD_BASE}/properties/match`, formData, {
        method: 'POST'
      });

      const matchResult = response.data;
      console.log('Property matches:', matchResult);
      
      if (matchResult.matches && matchResult.matches.length > 0) {
        // Found property match via geolocation
        const bestMatch = matchResult.matches[0];
        setSelectedProperty(bestMatch);
        setUnitStatus({
          message: t('property.propertyMatchedSimple', { name: bestMatch.display_name }),
          type: 'success'
        });
        
        // Auto-proceed to task selection after successful match
        proceedToTaskSelection();
      } else {
        // No properties found via geolocation, request unit number input
        setUnitStatus({
          message: t('property.noPropertiesFound'),
          type: 'warning'
        });
      }

    } catch (error) {
      console.error('Property matching error:', error);
      setUnitStatus({
        message: t('property.locationMatchingFailed'),
        type: 'error'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation]);

  const matchUnitToProperty = async (inputUnit) => {
    if (!inputUnit.trim()) {
      setUnitStatus({
        message: t('property.enterValidUnit'),
        type: 'error'
      });
      return;
    }

    // Normalize the input unit number to handle various formats
    const normalizedUnit = inputUnit.trim();
    
    setIsUnitMatching(true);
    setUnitStatus({
      message: t('property.searchingProperty'),
      type: 'info'
    });

    try {
      // Try fuzzy matching with the backend using JSON
      const response = await apiClient.post(`${API_CONFIG.UPLOAD_BASE}/properties/match-address`, {
        address: normalizedUnit
      });

      const matchResult = response.data;
      console.log('Unit match result:', matchResult);

      if (matchResult.match) {
        // Found a match - proceed directly
        setSelectedProperty(matchResult.match);
        setUnitStatus({
          message: t('property.propertyMatched', { name: matchResult.match.display_name, confidence: matchResult.match.confidence }),
          type: 'success'
        });
        
        // Auto-proceed to task selection after successful match
        proceedToTaskSelection();
      } else {
        // No match found - show confirmation dialog instead of auto-proceeding
        setPendingUnitInput(normalizedUnit);
        setShowNoMatchConfirmation(true);
        setUnitStatus({
          message: t('property.noMatchFound', { unit: normalizedUnit }),
          type: 'warning'
        });
      }

    } catch (error) {
      console.error('Unit matching error:', error);
      
      // On error, show confirmation dialog
      setPendingUnitInput(normalizedUnit);
      setShowNoMatchConfirmation(true);
      setUnitStatus({
        message: t('property.errorSearching', { unit: normalizedUnit }),
        type: 'error'
      });
    } finally {
      setIsUnitMatching(false);
    }
  };

  const showAddressInputForm = () => {
    // Show address input form with pre-filled unit number
    setFullAddress(pendingUnitInput);
    setShowNoMatchConfirmation(false);
    setShowAddressInput(true);
  };

  const confirmFullAddress = () => {
    // User entered a full address
    const trimmedAddress = fullAddress.trim();
    if (!trimmedAddress) {
      return;
    }

    setSelectedProperty({
      property_id: null,
      display_name: trimmedAddress,
      address: trimmedAddress,
      confidence: 'user_input'
    });
    
    setUnitStatus({
      message: t('property.usingAddress', { address: trimmedAddress }),
      type: 'success'
    });
    
    setShowAddressInput(false);
    setFullAddress('');
    setPendingUnitInput('');
    proceedToTaskSelection();
  };

  const cancelAddressInput = () => {
    // Go back to confirmation dialog
    setShowAddressInput(false);
    setFullAddress('');
    setShowNoMatchConfirmation(true);
  };

  const cancelNoMatchProperty = () => {
    // User wants to go back to unit input - clear the dialog state and reset to input form
    setShowNoMatchConfirmation(false);
    setShowAddressInput(false);
    setPendingUnitInput('');
    setUnitNumber('');
    setSelectedProperty(null);
    setUnitStatus({
      message: t('property.enterUnitOrAddress'),
      type: 'warning'
    });
  };

  const handleUnitSubmit = (e) => {
    e.preventDefault();
    matchUnitToProperty(unitNumber);
  };

  const proceedToTaskSelection = useCallback(() => {
    // Clear any existing property-related status messages before proceeding
    setStatusMessages(prev => prev.filter(msg => 
      !msg.message.includes('Property matched:') && 
      !msg.message.includes('No match found') &&
      !msg.message.includes('Using entered unit:')
    ));
    navigateToSection('setup');
  }, []);

  // proceedToCamera function removed as it's no longer needed

  // EXIF validation and extraction functions
  const validateImageHasRichExif = (file) => {
    return new Promise((resolve) => {
      if (!window.EXIF) {
        console.warn('EXIF.js not loaded, rejecting to be safe');
        resolve(false);
        return;
      }

      window.EXIF.getData(file, function() {
        try {
          const make = window.EXIF.getTag(this, 'Make');
          const model = window.EXIF.getTag(this, 'Model');

          if (make && model) {
            console.log(`✅ Valid gallery photo: ${make} ${model}`);
            resolve(true);
            return;
          }

          const allTags = window.EXIF.getAllTags(this);
          const meaningfulTags = Object.keys(allTags).filter(tag => 
            !['undefined', 'MakerNote', 'UserComment', 'ColorSpace', 'ExifVersion'].includes(tag) &&
            allTags[tag] !== null && allTags[tag] !== undefined
          );

          if (meaningfulTags.length >= 10) {
            console.log(`✅ Rich EXIF found: ${meaningfulTags.length} tags`);
            resolve(true);
            return;
          }

          console.log(`❌ INSUFFICIENT EXIF - Camera capture detected. Make: ${make}, Model: ${model}, Tags: ${meaningfulTags.length}`);
          resolve(false);

        } catch (error) {
          console.error('Error validating EXIF:', error);
          resolve(false);
        }
      });
    });
  };

  const extractExifData = (file) => {
    return new Promise((resolve) => {
      if (!window.EXIF || !file.type.startsWith('image/')) {
        resolve(null);
        return;
      }

      window.EXIF.getData(file, function() {
        try {
          const exifData = {
            camera: {},
            settings: {},
            gps: {},
            timestamp: {},
            raw: {}
          };

          // Camera information
          exifData.camera.make = window.EXIF.getTag(this, 'Make') || null;
          exifData.camera.model = window.EXIF.getTag(this, 'Model') || null;
          exifData.camera.lens = window.EXIF.getTag(this, 'LensModel') || null;
          exifData.camera.software = window.EXIF.getTag(this, 'Software') || null;

          // Camera settings
          exifData.settings.fNumber = window.EXIF.getTag(this, 'FNumber') || null;
          exifData.settings.exposureTime = window.EXIF.getTag(this, 'ExposureTime') || null;
          exifData.settings.iso = window.EXIF.getTag(this, 'ISOSpeedRatings') || null;
          exifData.settings.focalLength = window.EXIF.getTag(this, 'FocalLength') || null;
          exifData.settings.flash = window.EXIF.getTag(this, 'Flash') || null;
          exifData.settings.whiteBalance = window.EXIF.getTag(this, 'WhiteBalance') || null;

          // GPS data
          const gpsLat = window.EXIF.getTag(this, 'GPSLatitude');
          const gpsLatRef = window.EXIF.getTag(this, 'GPSLatitudeRef');
          const gpsLon = window.EXIF.getTag(this, 'GPSLongitude');
          const gpsLonRef = window.EXIF.getTag(this, 'GPSLongitudeRef');

          if (gpsLat && gpsLon) {
            const latDecimal = convertDMSToDD(gpsLat, gpsLatRef);
            const lonDecimal = convertDMSToDD(gpsLon, gpsLonRef);
            
            exifData.gps.latitude = latDecimal;
            exifData.gps.longitude = lonDecimal;
            exifData.gps.altitude = window.EXIF.getTag(this, 'GPSAltitude') || null;
          }

          // Timestamp information
          exifData.timestamp.dateTime = window.EXIF.getTag(this, 'DateTime') || null;
          exifData.timestamp.dateTimeOriginal = window.EXIF.getTag(this, 'DateTimeOriginal') || null;

          // Store all raw EXIF tags
          exifData.raw = window.EXIF.getAllTags(this);

          console.log('📸 Extracted EXIF data:', exifData);
          resolve(exifData);

        } catch (error) {
          console.error('Error extracting EXIF data:', error);
          resolve(null);
        }
      });
    });
  };

  const convertDMSToDD = (dms, ref) => {
    if (!dms || dms.length !== 3) return null;
    let dd = dms[0] + dms[1]/60 + dms[2]/3600;
    if (ref === 'S' || ref === 'W') dd = dd * -1;
    return dd;
  };

  // Photo upload functionality
  const handlePhotoUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    showStatus(t('status.validatingPhotos'), 'info');

    try {
      const validFiles = [];
      const rejectedFiles = [];

      // Validate all files
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Validate file type
        if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
          rejectedFiles.push({file, reason: 'only images and videos are allowed'});
          continue;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          rejectedFiles.push({file, reason: 'file too large (max 50MB)'});
          continue;
        }

        // For images, enforce gallery-only uploads by checking EXIF
        if (file.type.startsWith('image/')) {
          const hasValidExif = await validateImageHasRichExif(file);
          if (!hasValidExif) {
            rejectedFiles.push({file, reason: 'missing camera metadata - must upload from Photo Library'});
            continue;
          }
        }

        validFiles.push(file);
      }

      // Show rejection messages
      if (rejectedFiles.length > 0) {
        const cameraRejected = rejectedFiles.filter(r => r.reason.includes('missing camera metadata'));
        
        if (cameraRejected.length > 0) {
          event.target.value = '';
          showStatus(t('status.rejectedCamera', { count: cameraRejected.length }), 'error', 10000);
          return;
        }

        for (const rejected of rejectedFiles) {
          showStatus(t('status.rejectedFile', { filename: rejected.file.name, reason: rejected.reason }), 'error', 6000);
        }
      }

      // Upload valid files
      if (validFiles.length > 0) {
        showStatus(t('status.photosValidated', { count: validFiles.length }), 'success', 3000);
        
        // Show single upload progress message instead of per-file messages
        const uploadingId = showStatus(t('status.uploading', { count: validFiles.length }), 'info', 0);
        
        try {
          for (const file of validFiles) {
            await uploadPhotoFile(file);
          }
          
          // Dismiss the uploading message
          if (uploadingId) {
            dismissStatus(uploadingId);
          }
        } catch (error) {
          // Dismiss the uploading message on error
          if (uploadingId) {
            dismissStatus(uploadingId);
          }
          throw error;
        }
        
        showStatus(t('status.uploadedSuccessfully', { count: validFiles.length }), 'success', 4000);
      }

      event.target.value = '';

    } catch (error) {
      console.error('Photo upload error:', error);
      showStatus(t('status.errorProcessingPhotos'), 'error');
      event.target.value = '';
    }
  };

  const uploadPhotoFile = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);

      // Extract EXIF metadata
      const exifData = await extractExifData(file);
      if (exifData) {
        formData.append('exif_data', JSON.stringify(exifData));
        console.log('📸 EXIF data extracted:', exifData);

        // Reduce EXIF metadata notifications to avoid spam
        const cameraInfo = exifData.camera.make && exifData.camera.model 
          ? `${exifData.camera.make} ${exifData.camera.model}`
          : t('errors.unknownCamera');
        // Only log to console instead of showing status for every file
        console.log(`📸 Found EXIF metadata: ${cameraInfo}`);
      }

      // Add location data
      if (userLocation) {
        formData.append('latitude', userLocation.latitude);
        formData.append('longitude', userLocation.longitude);
        if (userLocation.accuracy && typeof userLocation.accuracy === 'number') {
          formData.append('location_accuracy', userLocation.accuracy);
        }
      } else if (exifData && exifData.gps) {
        formData.append('latitude', exifData.gps.latitude);
        formData.append('longitude', exifData.gps.longitude);
        if (exifData.gps.accuracy && typeof exifData.gps.accuracy === 'number') {
          formData.append('location_accuracy', exifData.gps.accuracy);
        }
      }

      // Add device info
      formData.append('device_info', getDeviceInfo());

      // Add session context for sessionless upload
      if (sessionContext) {
        formData.append('property_address', sessionContext.property_address);
        formData.append('task_type', sessionContext.task_type);
        formData.append('task_date', sessionContext.task_date);
        
        // Add property_id if available
        if (selectedProperty?.property_id) {
          formData.append('property_id', selectedProperty.property_id.toString());
        }
        
        console.log('🏠 Including session context in upload:', sessionContext);
      } else {
        console.error('❌ No session context available for upload');
        throw new Error(t('errors.sessionContextRequired'));
      }

      const response = await createFormDataRequest(`${API_CONFIG.UPLOAD_BASE}/images`, formData, {
        method: 'POST'
      });

      const imageData = response.data;
      console.log('File uploaded:', imageData);

      setUploadedImages(prev => [...prev, imageData]);
      // Remove individual success messages to reduce notification spam
      // showStatus(`✅ ${file.name} uploaded successfully!`, 'success', 3000);

    } catch (error) {
      console.error('Upload error:', error);
      showStatus(t('status.failedToUpload', { filename: file.name }), 'error');
    }
  };

  const deleteImage = async (imageId) => {
    try {
      await apiClient.delete(`${API_CONFIG.UPLOAD_BASE}/images/${imageId}`);

      setUploadedImages(prev => prev.filter(img => img.image_id !== imageId));
      showStatus(t('status.imageDeleted'), 'success', 2000);

    } catch (error) {
      console.error('Delete error:', error);
      showStatus(t('status.failedToDelete'), 'error');
    }
  };

  const completeTask = async () => {
    if (uploadedImages.length === 0) {
      showStatus(t('status.captureOneImage'), 'warning');
      return;
    }

    updateButtonState('completeSession', true);

    try {
      // No server-side completion needed in sessionless approach
      // Just navigate to completion
      navigateToSection('completion');
      showStatus(t('status.taskCompletedSuccess'), 'success', 6000);

    } catch (error) {
      console.error('Task completion error:', error);
      showStatus(t('errors.failedToCompleteTask'), 'error');
    } finally {
      updateButtonState('completeSession', false);
    }
  };

  const resetApp = () => {
    setSessionContext(null);
    setSelectedProperty(null);
    setUploadedImages([]);
    setTaskType('');
    setCustomTaskType('');
    setUnitNumber('');
    setIsUnitMatching(false);
    setUnitStatus({
      message: t('property.enterUnitOrAddress'),
      type: 'info'
    });
    setCurrentSection('property');
    setNavigationHistory(['property']);
    setStatusMessages([]);
    setProgressTimers({}); // Clear progress timers on reset
    // Reset unit status to show location loading
    setUnitStatus({
      message: t('property.gettingLocation'),
      type: 'info'
    });
    requestLocation();
    // Don't reset initialWelcomeShown - it should stay true to prevent duplicate welcome messages
  };

  const renderButton = (key, children, onClick, disabled = false, className = '') => {
    const buttonState = buttonStates[key];
    const isLoading = buttonState?.isLoading;
    const customText = buttonState?.customText;

    return (
      <button
        className={className}
        onClick={onClick}
        disabled={disabled || isLoading}
      >
        {isLoading ? (
          <>
            <div className="spinner"></div> {t('ui.loading')}
          </>
        ) : (
          customText || children
        )}
      </button>
    );
  };

  return (
    <div className="mobile-upload-container">

      {/* Session Setup Section */}
      {currentSection === "setup" && (
        <div className="card">
          <h2 className="form-label">{t('setup.selectTaskType')}</h2>

          {/* Property confirmation moved to global notifications */}
          {selectedProperty && (
            <div className="property-confirmation" style={{ 
              background: '#d1fae5', 
              border: '1px solid #10b981', 
              borderRadius: '12px', 
              padding: '12px 16px', 
              marginBottom: '20px',
              color: '#065f46',
              fontWeight: '500'
            }}>
              {t('setup.propertyLabel', { name: selectedProperty.display_name })}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{t('setup.taskTypeLabel')}</label>
            <div className="task-type-buttons">
              <button
                type="button"
                className={`task-button ${
                  taskType === "checkout-inspection" ? "active" : ""
                }`}
                onClick={() => {
                  setTaskType("checkout-inspection");
                  // Auto-start task for checkout-inspection
                  showStatus(t('setup.startingCheckout'), 'info', 2000);
                  setTimeout(() => startTaskWithType("checkout-inspection"), 100);
                }}
              >
                {t('setup.checkoutInspection')}
              </button>
              <button
                type="button"
                className={`task-button ${
                  taskType === "post-cleaning" ? "active" : ""
                }`}
                onClick={() => {
                  setTaskType("post-cleaning");
                  // Auto-start task for post-cleaning
                  showStatus(t('setup.startingCleaning'), 'info', 2000);
                  setTimeout(() => startTaskWithType("post-cleaning"), 100);
                }}
              >
                {t('setup.postCleaning')}
              </button>
              <button
                type="button"
                className={`task-button ${
                  taskType === "other" ? "active" : ""
                }`}
                onClick={() => setTaskType("other")}
              >
                {t('setup.others')}
              </button>
            </div>
          </div>

          {taskType === "other" && (
            <>
              <div className="form-group">
                <label className="form-label" htmlFor="custom-type">
                  {t('setup.customTaskType')}
                </label>
                <input
                  type="text"
                  id="custom-type"
                  className="form-input"
                  placeholder={t('setup.customTaskPlaceholder')}
                  value={customTaskType}
                  onChange={(e) => setCustomTaskType(e.target.value)}
                />
              </div>
              
              {renderButton(
                "startSession", 
                t('setup.startTask'),
                startTask,
                false,
                "btn btn-primary btn-full"
              )}
            </>
          )}
        </div>
      )}

      {/* Property Matching Section */}
      {currentSection === "property" && (
        <div className="card">
          <h2 className="form-label">{t('property.identification')}</h2>
          <div className="unit-status-display" style={{ 
            background: unitStatus.type === 'success' ? '#d1fae5' : unitStatus.type === 'error' ? '#fee2e2' : unitStatus.type === 'warning' ? '#fef3c7' : '#dbeafe',
            border: `1px solid ${unitStatus.type === 'success' ? '#10b981' : unitStatus.type === 'error' ? '#ef4444' : unitStatus.type === 'warning' ? '#f59e0b' : '#3b82f6'}`,
            color: unitStatus.type === 'success' ? '#065f46' : unitStatus.type === 'error' ? '#991b1b' : unitStatus.type === 'warning' ? '#792705' : '#1e40af',
            borderRadius: '12px',
            padding: '12px 16px',
            margin: '16px 0',
            fontWeight: '500'
          }}>
            {unitStatus.message}
          </div>

          {/* Show unit input if geolocation failed or no match */}
          {!selectedProperty && !showNoMatchConfirmation && !showAddressInput &&
            (unitStatus.type === "warning" || unitStatus.type === "error") && (
              <form onSubmit={handleUnitSubmit} className="form-group">
                <label className="form-label" htmlFor="unit-number">
                  {t('property.unitLabel')}
                </label>
                <input
                  type="text"
                  id="unit-number"
                  className="form-input"
                  placeholder={t('property.unitPlaceholder')}
                  value={unitNumber}
                  onChange={(e) => setUnitNumber(e.target.value)}
                  disabled={isUnitMatching}
                />
                <div
                  className="text-small"
                  style={{
                    color: "#6b7280",
                    marginTop: "5px",
                    marginBottom: "10px",
                  }}
                >
                  {t('property.unitExamples')}
                </div>
                <button
                  type="submit"
                  className="btn btn-primary btn-full"
                  disabled={!unitNumber.trim() || isUnitMatching}
                  style={{ marginTop: "10px" }}
                >
                  {isUnitMatching ? (
                    <>
                      <div className="spinner"></div> {t('property.searching')}
                    </>
                  ) : (
                    t('property.searchProperty')
                  )}
                </button>
              </form>
            )}

          {/* Show confirmation dialog when no match is found */}
          {showNoMatchConfirmation && (
            <div className="card" style={{ 
              background: '#fef3c7', 
              border: '2px solid #f59e0b',
              marginTop: '20px'
            }}>
              <h3 style={{ 
                color: '#92400e', 
                marginBottom: '15px',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                {t('property.noMatchTitle')}
              </h3>
              
              <p style={{ 
                color: '#92400e', 
                marginBottom: '20px',
                lineHeight: '1.5'
              }}>
                {t('property.noMatchMessage', { unit: pendingUnitInput })}
              </p>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ 
                  color: '#92400e', 
                  fontSize: '14px',
                  marginBottom: '15px',
                  lineHeight: '1.4'
                }}>
                  {t('property.whatToDo')}
                </p>

                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '10px' 
                }}>
                  <button
                    className="btn btn-secondary btn-full"
                    onClick={cancelNoMatchProperty}
                    style={{ fontSize: '15px' }}
                  >
                    {t('property.tryDifferentUnit')}
                  </button>
                  
                  <button
                    className="btn btn-primary btn-full"
                    onClick={showAddressInputForm}
                    style={{ fontSize: '15px' }}
                  >
                    {t('property.addNewProperty')}
                  </button>
                </div>
              </div>

              <div style={{ 
                fontSize: '13px', 
                color: '#78350f',
                background: 'rgba(255, 255, 255, 0.5)',
                padding: '10px',
                borderRadius: '8px',
                lineHeight: '1.4'
              }}>
                <strong>{t('property.tips')}</strong> {t('property.tipsText')}
              </div>
            </div>
          )}

          {/* Show full address input form */}
          {showAddressInput && (
            <div className="card" style={{ 
              background: '#eff6ff', 
              border: '2px solid #2563eb',
              marginTop: '20px'
            }}>
              <h3 style={{ 
                color: '#1e40af', 
                marginBottom: '15px',
                fontSize: '18px',
                fontWeight: '600'
              }}>
                {t('property.addNewPropertyTitle')}
              </h3>
              
              <p style={{ 
                color: '#1e40af', 
                marginBottom: '20px',
                lineHeight: '1.5'
              }}>
                {t('property.addNewPropertyMessage')}
              </p>

              <form onSubmit={(e) => {
                e.preventDefault();
                confirmFullAddress();
              }} className="form-group">
                <label className="form-label" htmlFor="full-address">
                  {t('property.fullAddressLabel')}
                </label>
                <input
                  type="text"
                  id="full-address"
                  className="form-input"
                  placeholder={t('property.fullAddressPlaceholder')}
                  value={fullAddress}
                  onChange={(e) => setFullAddress(e.target.value)}
                  style={{ marginBottom: '15px' }}
                />

                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '10px' 
                }}>
                  <button
                    type="submit"
                    className="btn btn-primary btn-full"
                    disabled={!fullAddress.trim()}
                    style={{ fontSize: '15px' }}
                  >
                    {t('property.confirmAddress')}
                  </button>
                  
                  <button
                    type="button"
                    className="btn btn-secondary btn-full"
                    onClick={cancelAddressInput}
                    style={{ fontSize: '15px' }}
                  >
                    {t('property.backToOptions')}
                  </button>
                </div>
              </form>

              <div style={{ 
                fontSize: '13px', 
                color: '#1e40af',
                background: 'rgba(255, 255, 255, 0.5)',
                padding: '10px',
                borderRadius: '8px',
                lineHeight: '1.4',
                marginTop: '15px'
              }}>
                <strong>{t('property.fullAddressTip')}</strong> {t('property.fullAddressTipText')}
              </div>
            </div>
          )}

          {/* Show proceed button if property is matched/selected */}
          {selectedProperty && unitStatus.type === "success" && (
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <p
                className="text-small"
                style={{ color: "#6b7280", marginBottom: "10px" }}
              >
                {t('property.propertyConfirmed')}
              </p>
              <button
                className="btn btn-success btn-full"
                onClick={proceedToTaskSelection}
              >
                {t('property.selectTaskType')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Camera Section */}
      {currentSection === "camera" && (
        <div className="card">
          <h2 className="form-label">{t('camera.captureImages')}</h2>

          <div className="camera-section">
            <div className="upload-section">
              <div className="upload-instructions">
                <h3 className="text-center mb-3">{t('camera.takeUploadPhotos')}</h3>
                <div className="instruction-steps">
                  <div className="step">
                    <span className="step-number">1</span>
                    <span className="step-text">
                      {t('camera.step1')}
                    </span>
                  </div>
                  <div className="step">
                    <span className="step-number">2</span>
                    <span className="step-text">
                      {t('camera.step2')}
                    </span>
                  </div>
                  <div className="step">
                    <span className="step-number">3</span>
                    <span className="step-text">
                      {t('camera.step3')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="photo-upload-section">
                <div className="warning-box">
                  <strong>{t('camera.importantWarning')}</strong> {t('camera.warningText')}
                </div>

                <input
                  type="file"
                  ref={photoInputRef}
                  accept=".jpg,.jpeg,.png,.heic,.heif,.mp4,.mov"
                  className="photo-upload-btn"
                  multiple
                  onChange={handlePhotoUpload}
              onClick={() => {
                    // Don't show status on every click to avoid spam
                  }}
                />
                <label
                  htmlFor="photo-input"
                  className="upload-label"
                  onClick={() => photoInputRef.current?.click()}
                >
                  <div className="upload-icon">📁</div>
                  <div className="upload-text">{t('camera.chooseFromLibrary')}</div>
                  <div className="upload-subtitle">
                    {t('camera.preservesMetadata')}
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Image Grid */}
          <div className="image-grid">
            {uploadedImages.map((image) => (
              <div key={image.image_id} className="image-item">
                <img
                  src={image.thumbnail_url || image.file_url}
                  alt={image.original_filename}
                  className="loaded"
                />
                <button
                  className="image-delete"
                  onClick={() => deleteImage(image.image_id)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <div className="text-center text-small mb-4">
              <span>
                {t('camera.imagesUploaded', { count: uploadedImages.length, plural: uploadedImages.length !== 1 ? 's' : '' })}
              </span>
            </div>
            {renderButton(
              "completeSession",
              t('camera.completeTask'),
              completeTask,
              false,
              "btn btn-success btn-full"
            )}
          </div>
        </div>
      )}

      {/* Completion Section */}
      {currentSection === "completion" && (
        <div className="card text-center">
          <h2 className="form-label">{t('completion.taskComplete')}</h2>
          <div className="completion-status" style={{
            background: '#d1fae5',
            border: '1px solid #10b981',
            borderRadius: '12px',
            padding: '16px',
            color: '#065f46',
            fontWeight: '500'
          }}>
            <div>{t('completion.completedSuccessfully')}</div>
            <div className="text-small mt-4">
              {t('completion.imagesUploadedToFolder', { count: uploadedImages.length })}
            </div>
          </div>

          <button className="btn btn-primary btn-full mt-4" onClick={resetApp}>
            {t('completion.startNewTask')}
          </button>
        </div>
      )}

      {/* Status Messages */}
      <div className="status-messages">
        {statusMessages.map((status) => {
          const progress = progressTimers[status.id] || 0;
          const showProgress = status.timeout > 0;
          
          return (
            <div key={status.id} className={`status status-${status.type}`}>
              <span className="status-message">{status.message}</span>
              <button
                className="status-dismiss"
                onClick={() => dismissStatus(status.id)}
                aria-label={t('status.dismissNotification')}
              >
                ×
              </button>
              {showProgress && (
                <div className="status-progress-container">
                  <div 
                    className="status-progress-bar"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MobileUpload;
